import { CancelToken } from 'axios';
import { appConfig } from '../config/app-config';
import { FilterPayload } from '../domain/PastDueList/Filter';
import { TextTemplate } from '../domain/TextConversation/TextTemplate';
import { LogWorkedHistoryPayload } from '../domain/WorkedHistory/WorkedHistory';
import {
  ActivityPayload,
  TakeCommitmentInput,
  AgreementInfoResponse,
  AssignRoutePayload,
  FieldSheetResponse,
  GetCustomerFieldSheetsPayload,
  StoreRouteResponse,
  WorkedHistoryResponse,
  CustomerCommitmentResponse,
  CommunicationsDetailsResponse,
  CustomerPhoneInstructionPayload,
  CustomerInfoResponse,
  RecordCheck,
} from '../types/types';
import {
  FetchNotesResponse,
  AddNoteRequest,
  AddNoteResponse,
  SearchNotesRequest,
} from '../types/noteTypes';
import { getSelectedStore } from '../utils/utils';
import { client } from './client';
import { PriorityCustomerResponse } from '../domain/priorityFieldVisit/PriorityCustomerDTO';

const storeNumber = getSelectedStore();

export const getRetentionOffers = async (Payload: any) => {
  return await client(
    `retention/offers`,
    { method: 'POST', body: Payload },
    appConfig.apiUrls.customer
  );
};

export const GetRemoteLoginStatus = async (Payload: any) => {
  return await client(
    `cashmgmt/duo/auth`,
    { method: 'POST', body: Payload },
    appConfig.apiUrls.cashmgmt
  );
};

export const getCustomerColorCode = async (Payload: any) => {
  return await client(
    `customer/color/coding`,
    { method: 'POST', body: Payload },
    appConfig.apiUrls.customer
  );
};

export const postAgreementActivity = async (Payload: any) => {
  return await client(
    `agreement/activity`,
    { method: 'POST', body: Payload },
    appConfig.apiUrls.customer
  );
};

export const getPinDetails = async (Payload: any) => {
  return await client(
    `coworker`,
    { method: 'POST', body: Payload },
    appConfig.apiUrls.coworker
  );
};

export const getprofileaccess = async (Payload: any) => {
  return await client(
    `store-access`,
    { method: 'POST', body: Payload },
    appConfig.apiUrls.coworker
  );
};

export const getsecondfactor = async (Payload: any) => {
  return await client(
    `coworker-authentication`,
    { method: 'POST', body: Payload },
    appConfig.apiUrls.coworker
  );
};

export const getPastDueList = async (
  payload: FilterPayload,
  offset: number,
  limit: number,
  cancelToken?: CancelToken
) =>
  client(`past-due-list?limit=${limit}&offset=${offset}`, {
    method: 'POST',
    body: payload,
    cancelToken,
  });

export const getCustomerDetails = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  customerId: string,
  cancelToken?: CancelToken
): Promise<CustomerInfoResponse> => {
  return client(
    `customer/${customerId}`,
    {
      method: 'PUT',
      body: {
        storeNumber: storeNumber,
      },
      cancelToken,
    },
    appConfig.apiUrls.customer
  );
};

export interface LegalHoldStatusResponse {
  legalHoldStatusRes?: Array<{
    legalHoldStatus?: boolean | string;
    legalHoldReasonid?: string | number;
    refCode?: string;
  }>;
}

export const getLegalHoldStatus = (
  customerId: string,
  cancelToken?: CancelToken
): Promise<LegalHoldStatusResponse> =>
  client(
    `customer/${customerId}/legal-hold`,
    { method: 'GET', cancelToken },
    appConfig.apiUrls.customer
  );

export const getCustomerCommitment = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  customerId: string,
  cancelToken?: CancelToken
): Promise<CustomerCommitmentResponse> => {
  return client(
    `customers/${customerId}/details?oper=commitment&storeNumber=${storeNumber}`,
    {
      method: 'GET',
      cancelToken,
    }
  );
};

export const getCustomerAgreements = (
  customerId: string,
  cancelToken?: CancelToken
): Promise<AgreementInfoResponse> =>
  client(
    `customer/${customerId}/agreements?inActive=N`,
    {
      method: 'GET',
      cancelToken,
    },
    appConfig.apiUrls.customer
  );

export const getWorkedHistory = (
  storeNumber: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  customerId: string,
  cancelToken: CancelToken
): Promise<WorkedHistoryResponse> =>
  client(`stores/${storeNumber}/customers/${customerId}/work-history`, {
    method: 'GET',
    cancelToken,
  });

export const getStoreRoutes = (
  storeNumber: string,
  cancelToken?: CancelToken
): Promise<StoreRouteResponse> =>
  client(`store-routes/${storeNumber}`, {
    method: 'GET',
    cancelToken,
  });

export const getPriorityCustomers = (
  storeId: string,
  cancelToken?: CancelToken
): Promise<PriorityCustomerResponse> =>
  client(`getPriorityList/${storeId}`, {
    method: 'GET',
    cancelToken,
  });

export const logWorkedHistory = (
  payload: Partial<LogWorkedHistoryPayload>
): Promise<WorkedHistoryResponse> =>
  client('customer/logactivity', {
    method: 'POST',
    body: payload,
  });

export const getAgreementInfo = (
  customerId: string,
  cancelToken?: CancelToken
): Promise<AgreementInfoResponse> =>
  client(`customer/get-agr-pastdue/${customerId}`, {
    method: 'GET',
    cancelToken,
  });

export const getTextTemplates = (): Promise<TextTemplate[]> =>
  client('/text-message/get-text-templates', { method: 'GET' });

export const logActivity = (payload: ActivityPayload) =>
  client('/customer/logactivity', { method: 'POST', body: { ...payload } });

export const assignRoute = (payload: AssignRoutePayload) =>
  client('/customer/assignroute', { method: 'POST', body: { ...payload } });

export const getFieldSheets = (
  payload: GetCustomerFieldSheetsPayload
): Promise<FieldSheetResponse> =>
  client('customers/field-sheets', { method: 'POST', body: { ...payload } });

export const takeCommitment = (payload: TakeCommitmentInput) =>
  client('/customer/take-commitment', { method: 'POST', body: { ...payload } });

export const getCommunicationDetails = (
  customerId: string
): Promise<CommunicationsDetailsResponse> =>
  client(`/customers/${customerId}/communication-details`, { method: 'GET' });

export const addressValidator = (payload: any) =>
  client(
    'customer/address/validate',
    {
      method: 'POST',
      body: {
        ...payload,
      },
    },
    appConfig.apiUrls.customer
  );

export const updatePhoneInstructions = (
  payload: CustomerPhoneInstructionPayload
) => client('/customers', { method: 'PUT', body: { ...payload } });

export const updateCustomer = (payload: any) =>
  client('customers', {
    method: 'PUT',
    body: {
      ...payload,
    },
  });

export const getCustomerStatesList = (country = 'state_province') =>
  client(`references/entity?referenceCode=${country}`, {
    method: 'GET',
  });

export const calculatePaymentModule = async (payload: any) => {
  try {
    return await client(
      `payment/calculate/module`,
      {
        method: 'POST',
        data: { apiCall: 'Y', ...payload },
      },
      appConfig.apiUrls.payment
    );
  } catch (err: any) {
    return 'Error';
  }
};

export const getCustomerRecordCheck = (
  customerId: string,
  cancelToken?: CancelToken
): Promise<RecordCheck> => {
  return client(
    `customercheck/${customerId}/details`,
    {
      method: 'POST',
      cancelToken,
    },
    appConfig.apiUrls.customer
  );
};

export const RequestCustomerRecordChk = async (payload: any): Promise<any> => {
  return client(
    `customercheck/request/details`,
    {
      method: 'POST',
      body: {
        ...payload,
      },
    },
    appConfig.apiUrls.customer
  );
};

export const decryptData = async (payload: any) => {
  return await client(
    `manage/draweraudit`,
    { method: 'POST', body: payload },
    appConfig.apiUrls.cashmgmt
  );
};

export const recordCheckBoxLog = async (payload: any): Promise<any> => {
  return client(
    `customercheck/manage`,
    {
      method: 'POST',
      body: {
        ...payload,
      },
    },
    appConfig.apiUrls.micro
  );
};

export const GetCustomerLoyaltyProgram = async (gcid?: any): Promise<any> => {
  return client(
    `customer/loyalty/profile/${gcid}?lob=RTO&programName=RAC Loyalty Rewards`,
    {
      method: 'GET',
    },
    appConfig.apiUrls.customer
  );
};

export const addNote = async (
  customerId: string,
  noteText: string,
  noteType = 'STMT'
): Promise<AddNoteResponse | undefined> => {
  const storeNumber = getSelectedStore();

  const payload: AddNoteRequest = {
    customerId,
    note: noteText,
    customerNoteType: noteType,
    storeNumber,
  };

  return await client(
    'create',
    {
      method: 'POST',
      body: payload,
    },
    appConfig.apiUrls.customer
  );
};

export const searchNotes = async (
  customerId: string,
  searchQuery = '',
  limit = 20,
  offset = 0,
  cancelToken?: CancelToken
): Promise<FetchNotesResponse | undefined> => {
  const storeNumber = getSelectedStore();

  const payload: SearchNotesRequest = {
    customerId,
    customerNoteType: 'STMT',
    storeNumber,
    searchParam: searchQuery,
    limit,
    offset,
  };
  return await client(
    'searchnotes',
    {
      method: 'POST',
      body: payload,
      cancelToken,
    },
    appConfig.apiUrls.customer
  );
};

export const updateNote = async (
  customerId: string,
  note: string,
  customerNotesId: string
): Promise<any> => {
  const payload = {
    customerId,
    note,
    customerNotesId,
  };

  return await client(
    'updatenote',
    {
      method: 'POST',
      body: payload,
    },
    appConfig.apiUrls.customer
  );
};

export const removeNote = async (
  customerId: string,
  customerNotesId: string
): Promise<any> => {
  const payload = {
    customerId,
    customerNotesId,
  };

  return await client(
    'removenote',
    {
      method: 'POST',
      body: payload,
    },
    appConfig.apiUrls.customer
  );
};

export const getNoteDetails = async (
  customerNoteId: string,
  cancelToken?: CancelToken
): Promise<any> => {
  return await client(
    `getnote/${customerNoteId}`,
    {
      method: 'GET',
      cancelToken,
    },
    appConfig.apiUrls.customer
  );
};
